import { Popover, PopoverContent, PopoverPortal, PopoverTrigger } from "@radix-ui/react-popover";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "@/components/podkit/lib/cn";
import { type FC, type ReactElement, type ReactNode } from "react";
import { IconCrossSmall } from "central-icons/IconCrossSmall";
import { Button } from "@/components/flexkit/Button";
import useIsMobileViewport from "@/hooks/use-is-mobile-viewport";

type ChatInputPopoverProps = {
    trigger: ReactElement;
    content: ReactElement;
    title?: ReactNode;
    className?: string;
};

export const ChatInputPopover: FC<ChatInputPopoverProps> = ({ trigger, content, title, className }) => {
    const { isMobileViewport } = useIsMobileViewport();

    if (isMobileViewport) {
        return (
            <DialogPrimitive.Root>
                <DialogPrimitive.Trigger asChild>{trigger}</DialogPrimitive.Trigger>
                <DialogPrimitive.Portal>
                    <DialogPrimitive.Overlay
                        className={cn(
                            "fixed inset-0 z-50 bg-surface-primary/50 backdrop-blur-modal backdrop-opacity-modal",
                            "data-[state=open]:animate-in data-[state=closed]:animate-out",
                            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
                        )}
                    >
                        <DialogPrimitive.Content
                            onCloseAutoFocus={(e) => e.preventDefault()}
                            className={cn(
                                "fixed inset-0 z-50",
                                "flex flex-col overflow-hidden",
                                "app-background",
                                "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out",
                                "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
                            )}
                        >
                            <div className="flex h-14 flex-shrink-0 items-center justify-between border-b border-border-base bg-surface-primary px-4">
                                <DialogPrimitive.Title className="text-lg font-semibold text-content-primary">
                                    {title}
                                </DialogPrimitive.Title>
                                <DialogPrimitive.Close asChild>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="size-8 p-0 text-content-muted"
                                        aria-label="Close"
                                    >
                                        <IconCrossSmall />
                                    </Button>
                                </DialogPrimitive.Close>
                            </div>

                            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-surface-primary p-4">
                                {content}
                            </div>
                        </DialogPrimitive.Content>
                    </DialogPrimitive.Overlay>
                </DialogPrimitive.Portal>
            </DialogPrimitive.Root>
        );
    }

    return (
        <Popover modal={true}>
            <PopoverTrigger asChild>{trigger}</PopoverTrigger>
            <PopoverPortal>
                <PopoverContent
                    side="top"
                    align="start"
                    sideOffset={8}
                    avoidCollisions={false}
                    onCloseAutoFocus={(e) => e.preventDefault()}
                    className={cn(
                        "z-50 max-h-96 overflow-y-auto rounded-xl border border-border-base bg-surface-pure p-1 shadow-lg",
                        "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
                        className,
                    )}
                >
                    {content}
                </PopoverContent>
            </PopoverPortal>
        </Popover>
    );
};
